/* eslint-disable no-unused-vars */
import React, { useLayoutEffect, useState, useEffect, useRef } from 'react'
import styles from './styles.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import { ReactComponent as ClockIcon } from '../../../../../assets/icons/clock-icon.svg'
import { ReactComponent as AddIcon } from '../../../../../assets/icons/plus-ico.svg'
import { arrayOfTimesGeneral, convertNumberToTime } from '../../../../pages/Appointments/timeLines'
import { addNewAppointment, updateAppointment } from '../../../../../Redux/appointments/appointmentSlice'
import SearchableOperationEntry from './SearchableOperationEntry'
import SearchablePatientEntry from './SearchablePatientEntry'
import { toggleChildPopup, togglePopup } from '../../../../../Redux/popups/popupSlice'
import { useTranslation } from 'react-i18next'
import { getAllUsers } from '../../../../../Redux/users/usersSlice'
import { v4 as uuidv4 } from 'uuid'
import { addNewPatient } from '../../../../../Redux/patients/patientsSlice'
import SVGMouth from '../../../../pages/PatientPage/components/OrganData/SVGMouth'

const autoResizeTextarea = (e) => {
  e.target.style.height = 'auto';
  e.target.style.height = (e.target.scrollHeight) + 'px';
};

const index = ({ close, data }) => {
  const { t } = useTranslation() || ((text) => text)
  const dispatch = useDispatch()
  const { operations } = useSelector((state) => state.operations)
  const { patients } = useSelector((state) => state.patients)
  const { users } = useSelector((state) => state.users)
  const { date, time, apt, editing, patient_id, operation_id, creating, showMouth } = data || {}
  const [isNew, setIsNew] = useState(!apt?.is_ongoing)
  const [toggleNewPayment, setTogglePayment] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState('stomatology')
  const [selectedTeeth, setSelectedTeeth] = useState(apt?.teeth_list || [])
  const [selectedType, setSelectedType] = useState('all')
  const [selectedRoom, setSelectedRoom] = useState(apt?.room || '')
  const [feedback, setFeedback] = useState(apt?.feedback || '')
  const [examination, setExamination] = useState(apt?.examination || '')
  const [therapy, setTherapy] = useState(apt?.therapy || '')
  const [isFreeService, setIsFreeService] = useState(false)
  // Add state variables to track cost values
  const [serviceCostValue, setServiceCostValue] = useState(apt?.service_cost || 0)
  const [patientPayValue, setPatientPayValue] = useState(apt?.patient_pay || 0)
  const [differenceValue, setDifferenceValue] = useState((apt?.service_cost || 0) - (apt?.patient_pay || 0))

  const initialTime = !editing
    ? {
        from: time?.from || 8,
        to: time?.from ? time?.from + 0.5 : 8.5
      }
    : {
        from: apt?.from || 8,
        to: apt?.to || 8.5
      }
  const initDate = !editing ? date?.fullDate : apt?.date
  const [selectedTime, setSelectedTime] = useState(initialTime)
  const [selectedOperation, setSelectedOperation] = useState(null)
  const [selectedPatient, setSelectedPatient] = useState(null)
  const [selectedDoctor, setSelectedDoctor] = useState(null)
  // Patient selection should be closed by default
  const [patientSelectOpen, setPatientSelectOpen] = useState(false)
  // Add state for multiple services
  const [services, setServices] = useState(() => {
    if (apt?.services && Array.isArray(apt.services)) {
      // If we have existing services, create an array of service objects
      return apt.services.map((name, index) => ({ 
        id: index + 1, 
        name,
        price: 0 // Will be updated when operation is found
      }));
    } else if (apt?.name) {
      // If we have a single name, use that
      return [{ id: 1, name: apt.name, price: apt?.service_cost || 0 }];
    } else if (apt?.operation_name) {
      // If we have an operation name, use that
      return [{ id: 1, name: apt.operation_name, price: apt?.service_cost || 0 }];
    }
    // Default for new appointments
    return [{ id: 1, name: '', price: 0 }];
  });
  const [serviceMenuOpen, setServiceMenuOpen] = useState({})
  const [serviceSearchInput, setServiceSearchInput] = useState('')
  const menuRef = useRef(null)

  // Determine if patient name should be locked (not editable)
  const lockPatientName = data?.lockPatientName;

  useLayoutEffect(() => {
    if (apt && editing && operations) {
      setSelectedOperation(operations.find((op) => op.id === apt.operation_id))
      setSelectedPatient(patients.find((pat) => pat.id === apt.patient_id))
      setSelectedDoctor(users.find((user) => user.id === apt.doctor_id))
      setSelectedCategory(apt.category || 'stomatology')
      if (apt.teeth_list) setSelectedTeeth(apt.teeth_list)
      if (apt.room) setSelectedRoom(apt.room)
      if (apt.feedback) setFeedback(apt.feedback)
      if (apt.examination) setExamination(apt.examination)
      if (apt.therapy) setTherapy(apt.therapy)
      // Initialize cost values from existing appointment
      setServiceCostValue(apt.service_cost || 0)
      setPatientPayValue(apt.patient_pay || 0)
      setDifferenceValue((apt.service_cost || 0) - (apt.patient_pay || 0))
      
      // Update service prices from operations data if available
      if (apt.services && Array.isArray(apt.services) && operations.length > 0) {
        const updatedServices = apt.services.map((serviceName, index) => {
          const matchingOperation = operations.find(op => op.op_name === serviceName);
          return {
            id: index + 1,
            name: serviceName,
            price: matchingOperation?.price || 0
          };
        });
        setServices(updatedServices);
        
        // Recalculate total cost
        const totalCost = updatedServices.reduce((sum, service) => sum + (parseFloat(service.price) || 0), 0);
        if (totalCost > 0) {
          setServiceCostValue(totalCost);
          setDifferenceValue(totalCost - (apt.patient_pay || 0));
        }
      }
    }
  }, [operations, patients, users, editing, apt])

  // Update the cost when the selected operation changes
  useEffect(() => {
    if (selectedOperation) {
      // If there are already services with prices, calculate total
      const currentServicesTotal = services.reduce((sum, service) => sum + (parseFloat(service.price) || 0), 0);
      
      if (currentServicesTotal > 0) {
        // Use the sum of all services if available
        setServiceCostValue(currentServicesTotal);
        setDifferenceValue(currentServicesTotal - patientPayValue);
      } else {
        // Fallback to operation price if no services have prices
        const operationPrice = selectedOperation.price || 0;
        setServiceCostValue(operationPrice);
        setDifferenceValue(operationPrice - patientPayValue);
      }
    }
  }, [selectedOperation, services, patientPayValue]);

  // Handle automatic patient selection when creating from patient page
  useEffect(() => {
    if (creating && patient_id && patients?.length) {
      const patient = patients.find((p) => p.id === patient_id)
      if (patient) {
        setSelectedPatient(patient)
        setSelectedCategory(patient.category || 'stomatology')
      }
    }
    
    if (creating && operation_id && operations?.length) {
      const operation = operations.find((op) => op.id === operation_id)
      if (operation) {
        setSelectedOperation(operation)
        setSelectedCategory(operation.category || 'stomatology')
      }
    }
  }, [creating, patient_id, operation_id, patients, operations])

  useEffect(() => {
    if (!users?.length) {
      dispatch(getAllUsers())
    }
  }, [users, dispatch])

  const handleTimeChange = (e, type) => {
    const { value } = e.target
    const newTimeObject = { ...selectedTime }
    newTimeObject[type] = parseFloat(value)
    
    // Ensure the time range is valid
    if (newTimeObject.from >= newTimeObject.to) {
      if (type === 'from') {
        newTimeObject.to = newTimeObject.from + 0.5
      } else {
        newTimeObject.from = newTimeObject.to - 0.5
      }
    }
    
    setSelectedTime(newTimeObject)
  }

  const deleteApt = () => {
    dispatch(togglePopup({ pointer: 3, data: { ...apt, deleteData: true } }))
  }

  const addNewService = () => {
    setServices([...services, { id: services.length + 1, name: '', price: 0 }])
  }

  const deleteService = (serviceId) => {
    // Don't allow deleting the first service
    if (serviceId === 1) return;
    
    // Remove the service and recalculate total cost
    const updatedServices = services.filter(service => service.id !== serviceId);
    setServices(updatedServices);
    
    // Recalculate total cost
    const newTotalCost = updatedServices.reduce((sum, service) => sum + (parseFloat(service.price) || 0), 0);
    setServiceCostValue(newTotalCost);
    
    // Recalculate difference
    setDifferenceValue(newTotalCost - patientPayValue);
  }

  const handleServiceChange = (id, value) => {
    setServices(services.map(service => 
      service.id === id ? { ...service, name: value } : service
    ))
  }

  const onToothclick = (toothNumber) => {
    let newTeethArray = [...selectedTeeth];
    if (newTeethArray.includes(toothNumber)) {
      newTeethArray = newTeethArray.filter((tooth) => tooth !== toothNumber);
    } else {
      newTeethArray.push(toothNumber);
    }
    setSelectedTeeth(newTeethArray);
  }

  const submitData = async (e) => {
    e.preventDefault()
    
    // Prevent multiple form submissions
    if (e.target.dataset.submitting === 'true') {
      console.log('⚠️ Form submission blocked - already in progress');
      return;
    }
    e.target.dataset.submitting = 'true';
    
    const formData = new FormData(e.target)
    const selectedCategoryValue = formData.get('category')
    
    // Enforce patient selection when creating from patient page
    if (creating && patient_id && (!selectedPatient || selectedPatient.id !== patient_id)) {
      alert(t('cannot_change_patient') || 'Nuk mund të ndryshoni pacientin kur krijoni një takim nga faqja e pacientit')
      return
    }
    
    // Check if a patient is selected
    if (!selectedPatient) {
      alert(t('please_select_patient') || 'Ju lutemi zgjidhni një pacient')
      return
    }
    
    if (!isNew) {
      const prevOp = selectedPatient?.operations?.find((op) => op?.operation_id === selectedOperation?.id)
      if (!prevOp) {
        alert(t('patient_no_ongoing_operation') || 'Pacienti nuk e ka këtë operacion në vazhdim')
        return
      }
    }
    
    if (!selectedDoctor) {
      alert(t('please_select_doctor') || 'Ju lutemi zgjidhni një mjek')
      return
    }
    
    const prevArray = apt?.array_of_payments || []
    const pricedata = isNew ? Number(selectedOperation?.price) : 0
    const from = parseFloat(formData.get('time_from'))
    const to = parseFloat(formData.get('time_to'))
    
    // Get service names from the form and filter out empty ones
    const serviceNames = services
      .map(service => service.name)
      .filter(name => name && name.trim() !== '')
    
    // If no services are selected, use the operation name or a default
    const mainServiceName = serviceNames.length > 0 
      ? serviceNames.join(', ') 
      : (selectedOperation?.op_name || t('untitled') || 'Pa titull')
    
    // Get medical notes from form
    const feedbackData = formData.get('feedback') || ''
    const examinationData = formData.get('examination') || ''
    const therapyData = formData.get('therapy') || ''
    
    // Get cost-related values - use state variables instead of form values
    // Calculate total cost from all services
    let finalServiceCost = 0;
    if (selectedCategory === 'pulmonology' && isFreeService) {
      finalServiceCost = 0;
    } else {
      // Sum up all service prices
      const totalFromServices = services.reduce((sum, service) => sum + (parseFloat(service.price) || 0), 0);
      finalServiceCost = totalFromServices > 0 ? totalFromServices : serviceCostValue;
    }
    
    // Set patient pay from state
    const finalPatientPay = selectedCategory === 'pulmonology' && isFreeService ? 0 : patientPayValue;
    
    // Calculate difference
    const finalDifference = finalServiceCost - finalPatientPay;
    
    // Shto ose përditëso pagesën në array_of_payments
    let updatedPayments = apt?.array_of_payments ? [...apt.array_of_payments] : [];
    
    // Debug logging to track payment processing
    console.log('🔍 PAYMENT DEBUG:', {
      finalPatientPay,
      formDate: formData.get('date'),
      existingPayments: updatedPayments.length,
      servicesCount: services.length,
      serviceNames: services.map(s => s.name),
      totalServiceCost: finalServiceCost
    });
    
    // Nëse ka pagesë të re (patient_pay > 0), shto atë në array
    if (finalPatientPay > 0 && formData.get('date')) {
      // Kontrollo nëse ka tashmë një pagesë për këtë datë
      const existingPaymentIndex = updatedPayments.findIndex(payment => payment.date === formData.get('date'));
      
      if (existingPaymentIndex >= 0) {
        // Nëse ka një pagesë ekzistuese për këtë datë, përditëso atë
        updatedPayments[existingPaymentIndex] = {
          amount: finalPatientPay,
          value: finalPatientPay,
          date: formData.get('date')
        };
        console.log('🔄 Updated existing payment:', updatedPayments[existingPaymentIndex]);
      } else {
        // Nëse nuk ka pagesë për këtë datë, shto një të re
        const newPayment = {
          amount: finalPatientPay,
          value: finalPatientPay,
          date: formData.get('date')
        };
        updatedPayments.push(newPayment);
        console.log('➕ Added new payment:', newPayment);
      }
    }
    
    // Llogarit totalin e vërtetë nga array
    const totalFromPayments = updatedPayments.reduce((sum, payment) => 
      sum + (Number(payment.amount) || Number(payment.value) || 0), 0);

    const formObject = {
      date: formData.get('date'),
      from,
      to,
      fromStr: convertNumberToTime(from),
      toStr: convertNumberToTime(to),
      name: mainServiceName,
      services: serviceNames,
      category: formData.get('category') || selectedOperation?.category || 'N/A',
      operation_id: selectedOperation?.id || '',
      operation_name: selectedOperation?.op_name || '',
      color: selectedDoctor?.color || selectedOperation?.color || '',
      main_issue: formData.get('main_issue') || '',
      patient_id: selectedPatient?.id || '',
      patient_name: selectedPatient?.full_name || '',
      doctor_id: selectedDoctor?.id || '',
      doctor_name: selectedDoctor?.displayName || '',
      doctor_signature: selectedDoctor?.signature || '',
      unit_multiplier: 1,
      array_of_payments: updatedPayments,
      price: pricedata,
      is_paid: selectedCategory === 'pulmonology' && isFreeService ? true : totalFromPayments >= finalServiceCost,
      is_ongoing: !isNew,
      room: selectedRoom,
      teeth_list: selectedTeeth,
      feedback: feedbackData,
      examination: examinationData,
      therapy: therapyData,
      service_cost: finalServiceCost,
      patient_pay: totalFromPayments, // Përdor totalin nga array
      difference: finalServiceCost - totalFromPayments,
      is_free_service: selectedCategory === 'pulmonology' && isFreeService,
      payment_date: updatedPayments.length > 0 ? updatedPayments[updatedPayments.length - 1].date : null,
      close
    }
    if (formObject?.array_of_payments?.length) {
      formObject.paid_amount = formObject.array_of_payments?.reduce((acc, cur) => acc + cur?.amount, 0) || 0;
      formObject.is_paid = Number(formObject.paid_amount) >= Number(formObject.price);
    }
    try {
      if (!editing) {
        dispatch(addNewAppointment({
          ...formObject,
          close: () => {
            e.target.dataset.submitting = 'false'; // Reset submission flag
            close();
            data?.onSuccess?.();
          }
        }))
      } else if (editing && apt) {
        dispatch(
          updateAppointment({
            ...formObject,
            id: apt.id,
            close: () => {
              e.target.dataset.submitting = 'false'; // Reset submission flag
              close();
              data?.onSuccess?.();
            }
          })
        )
      }
    } catch (error) {
      console.error('Form submission error:', error);
      e.target.dataset.submitting = 'false'; // Reset submission flag on error
    }
  }

  const addNewPatientToggle = () => {
    dispatch(
      toggleChildPopup({
        pointer: 1,
        data: {
          category: selectedCategory,
          opId: selectedOperation?.id,
          setSelectedPatient: setSelectedPatient
        }
      })
    )
  }

  const handleClickOutside = (e) => {
    if (menuRef.current && !menuRef.current.contains(e.target)) {
      setServiceMenuOpen({})
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const filteredServices = operations?.filter((op) => {
    const searchInputLower = serviceSearchInput.toLowerCase()
    const opNameLower = op.op_name?.toLowerCase()
    return (
      op.category === selectedCategory &&
      (opNameLower?.includes(searchInputLower) || searchInputLower === '')
    )
  })

  const handleServiceSelect = (operation, serviceId) => {
    // Update the specific service with the selected operation
    const updatedServices = services.map(service => 
      service.id === serviceId ? { ...service, name: operation.op_name, price: operation.price } : service
    );
    setServices(updatedServices);
    setServiceMenuOpen({});

    // Calculate total price from all services
    const totalPrice = updatedServices.reduce((sum, service) => sum + (parseFloat(service.price) || 0), 0);
    
    // Update the total service cost with the sum of all services
    setServiceCostValue(totalPrice);
    setDifferenceValue(totalPrice - patientPayValue);
  }

  return (
    <div className={styles.popup_wrapper}>
      <div className={styles.form_header}>
        <h2 className={styles.header_text}>{editing ? 'Edito Takimin' : 'Shto Termin të Ri'}</h2>
        <button className={styles.close_btn} onClick={close}>
          ✖
        </button>
      </div>
      <form className={styles.popup_from_container} onSubmit={submitData}>
        <div className={styles.form_grid}>
          {/* Date and Time section */}
          <div className={styles.form_entry}>
            <label className={styles.entry_label}>Data e terminit</label>
            <div className={styles.time_container}>
              <div className={styles.time_date_container}>
                <input
                  type='date'
                  name='date'
                  className={styles.entry_input}
                  defaultValue={initDate}
                  lang="en-GB"
                  onFocus={(e) => {
                    if (e.target.value) {
                      const date = new Date(e.target.value);
                      e.target.value = date.toLocaleDateString('en-GB');
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value) {
                      const [day, month, year] = e.target.value.split('/');
                      if (day && month && year) {
                        e.target.value = `${year}-${month}-${day}`;
                      }
                    }
                  }}
                />
                <ClockIcon className={styles.time_icon} />
              </div>
              <div className={styles.time_hr_container}>
                <span className={styles.time_indicator_of}>Nga</span>
                <select
                  className={styles.entry_input}
                  id='time_from'
                  name='time_from'
                  value={selectedTime?.from}
                  onChange={(e) => handleTimeChange(e, 'from')}
                  required
                >
                  {[...arrayOfTimesGeneral]?.slice(0, arrayOfTimesGeneral?.length - 1)?.map((time) => (
                    <option key={time.value} value={time.value}>
                      {time.displayValue}
                    </option>
                  ))}
                </select>
                <span className={styles.time_indicator_of}>Deri</span>
                <select
                  id='time_to'
                  name='time_to'
                  value={selectedTime?.to}
                  onChange={(e) => handleTimeChange(e, 'to')}
                  className={styles.entry_input}
                  required
                >
                  {[...arrayOfTimesGeneral]?.slice(1)?.map((time) => (
                    <option key={time.value} value={time.value}>
                      {time.displayValue}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Side by side layout for Category and Employee */}
          <div className={styles.form_row}>
            {/* Category select field */}
            <div className={styles.form_entry}>
              <label className={styles.entry_label} htmlFor="category">
                Kategoria
              </label>
              <select
                id="category"
                name="category"
                className={styles.entry_input}
                value={selectedCategory}
                onChange={e => setSelectedCategory(e.target.value)}
                required
                disabled={true}
              >
                <option value="stomatology">Stomatologji</option>
              </select>
            </div>
            
            {/* Employee select field */}
            <div className={styles.form_entry}>
              <label className={styles.entry_label} htmlFor='employee_select'>
                Mjeku
              </label>
              <select
                id='employee_select'
                name='employee_select'
                className={styles.entry_input}
                value={selectedDoctor ? selectedDoctor.id : ''}
                onChange={e => {
                  const userObj = users.find(u => u.id === e.target.value)
                  setSelectedDoctor(userObj)
                }}
                required
              >
                <option value=''>-- Zgjidh mjekun --</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>{user.displayName} {user.role ? `(${user.role})` : ''}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Link/select patient field */}
          <div className={`${styles.form_entry} ${styles.patient_entry}`}>
            <SearchablePatientEntry
              selectedPatient={selectedPatient}
              setSelectedPatient={setSelectedPatient}
              editing={editing}
              patients={patients}
              isNew={isNew}
              addNewPatientToggle={addNewPatientToggle}
              t={t}
              disablePatientChange={(creating && patient_id) || lockPatientName}
              initialOpen={patientSelectOpen}
              setMenuOpen={setPatientSelectOpen}
              selectedCategory={selectedCategory}
            />
          </div>
          
          {/* Room Selection */}
          <div className={styles.form_entry} style={{ display: 'none'}}>
            <label className={styles.entry_label}>
              Dhoma
            </label>
            <select 
              className={styles.entry_input}
              name="room"
              value={selectedRoom} 
              onChange={(e) => setSelectedRoom(e.target.value)}
            >
              <option value="">Zgjidh dhomën</option>
              <option value="Dhoma 1">Dhoma 1</option>
              <option value="Dhoma 2">Dhoma 2</option>
              <option value="Dhoma 3">Dhoma 3</option>
              <option value="Dhoma 4">Dhoma 4</option>
              <option value="Dhoma 5">Dhoma 5</option>
            </select>
          </div>

          {/* Teeth Selection - show for Stomatology OR if showMouth flag is true */}
          {(selectedCategory === 'stomatology' && !isNew && (showMouth === true && selectedCategory !== 'pulmonology')) && (
            <div className={styles.form_entry}>
              <label className={styles.entry_label}>
                Zgjedhja e dhëmbëve
              </label>
              <div className={styles.teeth_selection_container}>
                <div className={styles.teeth_buttons}>
                  <button
                    type="button"
                    className={`${styles.teeth_button} ${selectedType === 'all' ? styles.active : ''}`}
                    onClick={() => setSelectedType('all')}
                  >
                    Të gjitha
                  </button>
                  <button
                    type="button"
                    className={`${styles.teeth_button} ${selectedType === 'adult' ? styles.active : ''}`}
                    onClick={() => setSelectedType('adult')}
                  >
                    Rritur
                  </button>
                  <button
                    type="button"
                    className={`${styles.teeth_button} ${selectedType === 'child' ? styles.active : ''}`}
                    onClick={() => setSelectedType('child')}
                  >
                    Fëmijë
                  </button>
                </div>
                <div className={styles.teeth_chart}>
                  <SVGMouth selectedType={selectedType} selectedTeeth={selectedTeeth} onToothclick={onToothclick} />
                </div>
              </div>
            </div>
          )}
          
          {/* Operation entry */}
          <div className={`${styles.form_entry} ${styles.operation_entry}`}>
            {!isNew && (
              <SearchableOperationEntry
                selectedOperation={selectedOperation}
                setSelectedOperation={setSelectedOperation}
                editing={editing}
                operations={operations}
                apt={apt}
                isNew={isNew}
                setIsNew={setIsNew}
                t={t}
              />
            )}
          </div>
          
          {/* Multiple service names entry */}
          <div className={styles.form_entry}>
            <div className={styles.service_header}>
              <label className={styles.entry_label}>
                Emri i shërbimit
              </label>
              <button 
                type="button" 
                className={styles.add_service_btn}
                onClick={addNewService}
                title="Shto një shërbim tjetër"
              >
                <AddIcon /> Shto shërbim
              </button>
            </div>
            
            {services.map((service) => (
              <div key={service.id} className={styles.service_item} ref={menuRef}>
                <div className={`${styles.op_selector_container} ${serviceMenuOpen[service.id] ? styles.active_menu : ''}`}>
                  <div className={styles.service_input_container}>
                    <input
                      type='text'
                      id={`service_${service.id}`}
                      name={`service_${service.id}`}
                      value={service.name}
                      placeholder="Zgjidh shërbimin..."
                      className={styles.selector_input}
                      autoComplete='off'
                      onClick={() => setServiceMenuOpen({ [service.id]: !serviceMenuOpen[service.id] })}
                      readOnly
                      required
                    />
                    {service.id !== 1 && (
                      <button
                        type="button"
                        className={styles.delete_service_btn}
                        onClick={() => deleteService(service.id)}
                        title="Fshi shërbimin"
                      >
                        ✖
                      </button>
                    )}
                  </div>
                  <div className={styles.op_menu_container}>
                    <div className={styles.op_menu_search}>
                      <input
                        className={styles.op_menu_search_input}
                        value={serviceSearchInput}
                        placeholder={t('search')}
                        onChange={(e) => setServiceSearchInput(e.target.value)}
                      />
                    </div>
                    <ul className={styles.op_menu_list_container}>
                      {filteredServices?.length ? (
                        filteredServices?.map((op) => (
                          <li
                            key={op.id}
                            className={styles.op_menu_item}
                            onClick={() => handleServiceSelect(op, service.id)}
                          >
                            <span className={styles.op_menu_text}>
                              {op.op_name} - {op.price ? `${op.price}€` : 'Pa çmim'}
                            </span>
                          </li>
                        ))
                      ) : (
                        <li className={`${styles.op_menu_item} ${styles.disabled_entry}`} disabled>
                          <span className={styles.op_menu_text} style={{ textAlign: 'center', width: '100%' }}>
                            {t('no_services')}
                          </span>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Free Service Checkbox - Only show for Pulmonology */}
          {selectedCategory === 'pulmonology' && (
            <div className={styles.free_service_container}>
              <label className={styles.free_service_label}>
                <input
                  type="checkbox"
                  checked={isFreeService}
                  onChange={(e) => {
                    setIsFreeService(e.target.checked)
                    if (e.target.checked) {
                      setServiceCostValue(0)
                      setPatientPayValue(0)
                      setDifferenceValue(0)
                    } else {
                      // Reset to original values if unchecking
                      const operationPrice = selectedOperation?.price || 0
                      setServiceCostValue(operationPrice)
                      setPatientPayValue(0)
                      setDifferenceValue(operationPrice)
                    }
                  }}
                />
                <span className={styles.free_service_text}>
                  {t('free_service')}
                </span>
              </label>
              <div className={styles.free_service_info}>
                {t('free_service_info')}
              </div>
            </div>
          )}
          
          {/* Hidden cost-related fields - using value from state instead of defaultValue */}
          <input
            type='hidden'
            id='service_cost'
            name='service_cost'
            value={serviceCostValue}
            onChange={(e) => setServiceCostValue(parseFloat(e.target.value) || 0)}
          />
          <input
            type='hidden'
            id='patient_pay'
            name='patient_pay'
            value={patientPayValue}
            onChange={(e) => setPatientPayValue(parseFloat(e.target.value) || 0)}
          />
          <input
            type='hidden'
            id='difference'
            name='difference'
            value={differenceValue}
            onChange={(e) => setDifferenceValue(parseFloat(e.target.value) || 0)}
          />
          
          {/* Medical Notes Section */}
          <div className={styles.medical_notes_section}>
            <div className={styles.section_title}>Të dhënat mjekësore</div>
            
            <div className={styles.form_entry}>
              <label className={styles.entry_label} htmlFor='feedback'>
                Komente
              </label>
              <textarea 
                value={feedback} 
                onChange={(e) => setFeedback(e.target.value)}
                className={styles.entry_input} 
                id='feedback' 
                name='feedback' 
                placeholder="Nuk ka komente..."
                onInput={autoResizeTextarea}
              />
            </div>
            
            {selectedCategory !== 'stomatology' && (
              <div className={styles.form_entry}>
                <label className={styles.entry_label} htmlFor='examination'>
                  Egzaminimi
                </label>
                <textarea 
                  value={examination} 
                  onChange={(e) => setExamination(e.target.value)}
                  className={styles.entry_input} 
                  id='examination' 
                  name='examination' 
                  placeholder="Nuk ka egzaminim..."
                  onInput={autoResizeTextarea}
                />
              </div>
            )}
            
            <div className={styles.form_entry}>
              <label className={styles.entry_label} htmlFor='therapy'>
                Terapia
              </label>
              <textarea 
                value={therapy} 
                onChange={(e) => setTherapy(e.target.value)}
                className={styles.entry_input} 
                id='therapy' 
                name='therapy' 
                placeholder="Nuk ka terapi..."
                onInput={autoResizeTextarea}
              />
            </div>
          </div>

          {/* Submit buttons */}
          <div className={`${styles.form_entry} ${styles.submit_container}`}>
            {editing && (
              <button type="button" className={styles.delete_btn} onClick={deleteApt}>
                Fshi
              </button>
            )}
            <div className={styles.submit_buttons_group}>
              <button type="button" className={styles.cancel_btn} onClick={close}>
                Anulo
              </button>
              <button type="submit" className={styles.submit_button}>
                {editing ? 'Përditëso' : 'Ruaj'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}
export default index
